// Quote section — renders the customer's current quote (rooms, line items,
// totals), and exposes the Approve / Decline / View T&Cs actions.
//
// Renders ONLY when `project.quote` is non-null.
//
// Source-of-truth references:
//   - customer-live.js:445–501   quote card list
//   - customer-live.js:504–609   quote details renderer (print window)
//   - customer-live.js:610–623   acceptQuote() — POST /api/customer/quotes/:ref/accept
//   - kitchenos-api/modules/customer_portal.js:1209–1239  server endpoint
//
// IMPORTANT: this section never displays trade prices. It only renders
// `lineTotal`, `subtotal`, `vatAmount`, `deliveryCharge` and
// `totalIncVat` — all customer-facing retail amounts that come from the
// already-margin-applied `CustomerQuote` shape in types.ts.

import { ApiError } from "@tkos/api-client";

import type {
  CustomerProject,
  CustomerQuote,
  CustomerQuoteItem,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./quote.css";

// ── Formatters ───────────────────────────────────────────────────────────────

const moneyFmt = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

const validUntilFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function money(n: number): string {
  if (!Number.isFinite(n)) return moneyFmt.format(0);
  return moneyFmt.format(n);
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Status pill mapping ─────────────────────────────────────────────────────

const STATUS_LABELS: Record<CustomerQuote["status"], string> = {
  draft: "Draft",
  sent: "Sent",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
};

const STATUS_PILL: Record<CustomerQuote["status"], string> = {
  draft: "cust-pill",
  sent: "cust-pill cust-pill-info",
  approved: "cust-pill cust-pill-ok",
  rejected: "cust-pill cust-pill-danger",
  expired: "cust-pill cust-pill-danger",
};

// ── Valid-until calculation ─────────────────────────────────────────────────

/** Resolve a quote's expiry to a timestamp (ms). Prefers the server-set
 * `expiresAt`; else computes from sent_at + valid_days. null if unknown. */
function expiryMs(q: CustomerQuote): number | null {
  if (q.expiresAt) {
    const t = Date.parse(q.expiresAt);
    if (Number.isFinite(t)) return t;
  }
  if (q.sentAt && Number.isFinite(q.validDays) && q.validDays > 0) {
    const sent = Date.parse(q.sentAt);
    if (Number.isFinite(sent)) {
      const d = new Date(sent);
      d.setDate(d.getDate() + q.validDays);
      return d.getTime();
    }
  }
  return null;
}

/** Formatted expiry info for display. `expired` is true once the date has
 * passed — under soft expiry the quote is still acceptable, so this only
 * drives the wording ("Expired on …" vs "Valid until …"), not the button. */
function expiryInfo(q: CustomerQuote): { label: string; expired: boolean } | null {
  const ms = expiryMs(q);
  if (ms === null) return null;
  const date = validUntilFmt.format(new Date(ms));
  return { label: date, expired: ms < Date.now() };
}

/** Back-compat string label (used by the printable PDF). */
function validUntilLabel(q: CustomerQuote): string | null {
  return expiryInfo(q)?.label ?? null;
}

// ── Rooms / items grouping ──────────────────────────────────────────────────

interface GroupedRoom {
  id: string;
  name: string;
  subtotal: number;
  items: CustomerQuoteItem[];
}

/** Group quote items by room. Items whose `roomName` doesn't match a
 * room in `quote.rooms` go into a synthetic "Other" group at the end. */
function groupByRoom(quote: CustomerQuote): GroupedRoom[] {
  const groups: GroupedRoom[] = quote.rooms.map((r) => ({
    id: r.id,
    name: r.name,
    subtotal: r.subtotal,
    items: [],
  }));
  const byName = new Map<string, GroupedRoom>();
  for (const g of groups) byName.set(g.name, g);

  const orphan: GroupedRoom = {
    id: "__orphan__",
    name: "Other items",
    subtotal: 0,
    items: [],
  };

  for (const item of quote.items) {
    const room = item.roomName ? byName.get(item.roomName) : undefined;
    if (room) {
      room.items.push(item);
    } else {
      orphan.items.push(item);
      orphan.subtotal += Number.isFinite(item.lineTotal) ? item.lineTotal : 0;
    }
  }

  if (orphan.items.length > 0) groups.push(orphan);
  return groups;
}

/** Two-party address header (seller left, buyer right) + reference/date/expiry.
 *  Renders nothing for quotes sent before the header feature (header == null). */
function renderQuoteHeader(q: CustomerQuote): string {
  const h = q.header;
  if (!h) return "";
  const lines = (arr: string[]): string =>
    arr.filter((l) => l && l.trim() !== "").map((l) => `<div>${esc(l)}</div>`).join("");
  const sellerExtra = [
    h.seller.phone ? `Tel: ${h.seller.phone}` : "",
    h.seller.email ?? "",
    h.seller.vatNumber ? `VAT No: ${h.seller.vatNumber}` : "",
  ];
  const sentMs = q.sentAt ? Date.parse(q.sentAt) : NaN;
  const dateLabel = Number.isFinite(sentMs) ? validUntilFmt.format(new Date(sentMs)) : null;
  const exp = expiryInfo(q);
  return `
    <div class="quote-doc-header">
      <div class="quote-party quote-party--seller">
        ${h.seller.name ? `<div class="quote-party-name">${esc(h.seller.name)}</div>` : ""}
        ${lines(h.seller.addressLines)}
        ${lines(sellerExtra)}
      </div>
      <div class="quote-party quote-party--buyer">
        <div class="quote-party-label">Prepared for</div>
        ${h.buyer.name ? `<div class="quote-party-name">${esc(h.buyer.name)}</div>` : ""}
        ${lines(h.buyer.addressLines)}
      </div>
      <dl class="quote-doc-meta">
        <div><dt>Quote</dt><dd>${esc(q.reference)}</dd></div>
        ${dateLabel ? `<div><dt>Date</dt><dd>${esc(dateLabel)}</dd></div>` : ""}
        ${exp ? `<div><dt>${exp.expired ? "Expired" : "Valid until"}</dt><dd>${esc(exp.label)}</dd></div>` : ""}
      </dl>
    </div>
  `;
}

function renderItemRow(item: CustomerQuoteItem): string {
  const qty = Number.isFinite(item.quantity) ? item.quantity : 0;
  const unit = (item.unit ?? "").trim();
  const qtyUnit = unit !== "" ? `${qty} × ${esc(unit)}` : String(qty);
  return `
    <tr class="quote-item-row">
      <td class="quote-item-desc">${esc(item.description || "Item")}</td>
      <td class="quote-item-qty">${esc(qtyUnit)}</td>
      <td class="quote-item-total">${esc(money(item.lineTotal))}</td>
    </tr>
  `;
}

function renderRoomBlock(group: GroupedRoom): string {
  if (group.items.length === 0) return "";
  return `
    <div class="quote-room">
      <div class="quote-room-head">
        <h3 class="quote-room-name">${esc(group.name)}</h3>
        <span class="quote-room-subtotal">${esc(money(group.subtotal))}</span>
      </div>
      <table class="quote-items">
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col">Qty</th>
            <th scope="col" class="quote-item-total">Total</th>
          </tr>
        </thead>
        <tbody>
          ${group.items.map(renderItemRow).join("")}
        </tbody>
      </table>
    </div>
  `;
}

// ── Printable HTML for the Download PDF action ──────────────────────────────

/** Build a self-contained HTML document representing the quote, suitable
 *  for opening in a new tab and saving as PDF via the browser's print
 *  dialog. Mirrors customer-live.js:510-609. */
function buildPrintableQuoteHtml(
  q: CustomerQuote,
  retailerName: string,
  customerFirstName: string,
): string {
  const grouped = groupByRoom(q);
  const validUntil = validUntilLabel(q);
  const rows = grouped
    .filter((g) => g.items.length > 0)
    .map(
      (g) => `
      <tr><td colspan="3" class="room">${esc(g.name)}</td></tr>
      ${g.items
        .map(
          (it) => `
          <tr>
            <td>${esc(it.description || "Item")}</td>
            <td class="qty">${esc(
              (Number.isFinite(it.quantity) ? it.quantity : 0).toString() +
                (it.unit ? ` × ${it.unit}` : ""),
            )}</td>
            <td class="amt">${esc(money(it.lineTotal))}</td>
          </tr>`,
        )
        .join("")}
    `,
    )
    .join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Quote ${esc(q.reference)} — ${esc(retailerName)}</title>
<style>
  body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; color: #111; max-width: 720px; margin: 32px auto; padding: 0 24px; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0d8a72; padding-bottom: 16px; margin-bottom: 24px; }
  h1 { margin: 0; font-size: 24px; }
  .sub { color: #6b7280; font-size: 13px; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  th, td { padding: 8px 4px; text-align: left; border-bottom: 1px solid #eee; font-size: 13px; }
  th { color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; font-size: 11px; }
  td.qty, th.qty, td.amt, th.amt { text-align: right; font-variant-numeric: tabular-nums; }
  td.room { font-weight: 700; padding-top: 14px; background: #f6f7f5; }
  .totals { width: 100%; max-width: 320px; margin-left: auto; }
  .totals tr td { border: none; padding: 4px 0; }
  .totals .grand { font-weight: 700; font-size: 16px; border-top: 1px solid #ccc; padding-top: 8px; }
  .foot { margin-top: 32px; color: #6b7280; font-size: 12px; }
  .parties { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 20px; font-size: 12px; color: #374151; }
  .parties .label { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
  .parties .name { font-weight: 700; font-size: 13px; }
  .parties .buyer { text-align: right; }
  .note { font-size: 13px; color: #374151; line-height: 1.5; margin: 0 0 20px; }
  .note--closing { margin: 20px 0 0; }
  @media print { body { margin: 0; padding: 16px; } }
</style>
</head>
<body onload="window.print()">
  <header>
    <div>
      <h1>${esc(retailerName)}</h1>
      <div class="sub">Quote ${esc(q.reference)}${customerFirstName ? ` — for ${esc(customerFirstName)}` : ""}</div>
    </div>
    <div class="sub" style="text-align:right">
      ${q.sentAt ? `Issued ${esc(new Date(q.sentAt).toLocaleDateString("en-GB"))}<br/>` : ""}
      ${validUntil ? `Valid until ${esc(validUntil)}` : ""}
    </div>
  </header>
  ${q.header ? `
  <div class="parties">
    <div class="seller">
      ${q.header.seller.name ? `<div class="name">${esc(q.header.seller.name)}</div>` : ""}
      ${q.header.seller.addressLines.map((l) => `<div>${esc(l)}</div>`).join("")}
      ${q.header.seller.phone ? `<div>Tel: ${esc(q.header.seller.phone)}</div>` : ""}
      ${q.header.seller.email ? `<div>${esc(q.header.seller.email)}</div>` : ""}
      ${q.header.seller.vatNumber ? `<div>VAT No: ${esc(q.header.seller.vatNumber)}</div>` : ""}
    </div>
    <div class="buyer">
      <div class="label">Prepared for</div>
      ${q.header.buyer.name ? `<div class="name">${esc(q.header.buyer.name)}</div>` : ""}
      ${q.header.buyer.addressLines.map((l) => `<div>${esc(l)}</div>`).join("")}
    </div>
  </div>` : ""}
  ${q.introNote ? `<p class="note note--intro">${esc(q.introNote)}</p>` : ""}
  <table>
    <thead><tr><th>Item</th><th class="qty">Qty</th><th class="amt">Total</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <table class="totals">
    <tr><td>Subtotal</td><td class="amt">${esc(money(q.subtotal))}</td></tr>
    ${q.vatAmount > 0 ? `<tr><td>VAT</td><td class="amt">${esc(money(q.vatAmount))}</td></tr>` : ""}
    ${q.deliveryCharge > 0 ? `<tr><td>Delivery</td><td class="amt">${esc(money(q.deliveryCharge))}</td></tr>` : ""}
    <tr class="grand"><td>${q.vatAmount > 0 ? "Total (inc. VAT)" : "Total"}</td><td class="amt">${esc(money(q.totalIncVat))}</td></tr>
  </table>
  ${q.retailerVatNumber ? `<div class="sub" style="margin-top:8px">VAT No: ${esc(q.retailerVatNumber)}</div>` : ""}
  ${q.closingNote ? `<p class="note note--closing">${esc(q.closingNote)}</p>` : ""}
  ${q.termsConditions ? `<div class="foot"><strong>Terms &amp; conditions</strong><br/>${esc(q.termsConditions)}</div>` : ""}
</body>
</html>`;
}

// ── Section ──────────────────────────────────────────────────────────────────

const quoteSection: CustomerSection = {
  id: "quote",
  label: "Quote",

  shouldRender(project: CustomerProject): boolean {
    return (project.quotes && project.quotes.length > 0) || project.quote !== null;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    // Item 8 — render EVERY quote the customer has been sent (newest-first),
    // with a switcher to move between them. Each quote gets the full detail
    // card + approve/sign flow via mountQuoteDetail(). Single-quote projects
    // skip the switcher and look exactly as before.
    const quotes: CustomerQuote[] =
      ctx.project.quotes && ctx.project.quotes.length
        ? ctx.project.quotes
        : ctx.project.quote
        ? [ctx.project.quote]
        : [];
    if (quotes.length === 0) return () => {};

    let activeIdx = 0;
    let detailCleanup: SectionCleanup = () => {};
    const detailHost = document.createElement("div");
    detailHost.className = "quote-detail-host";

    let switcher: HTMLElement | null = null;
    if (quotes.length > 1) {
      switcher = document.createElement("nav");
      switcher.className = "quote-switcher";
      switcher.setAttribute("aria-label", "Your quotes");
      switcher.innerHTML = quotes
        .map(
          (q, i) => `
            <button type="button" class="quote-switcher-tab" data-idx="${i}" aria-pressed="${i === 0 ? "true" : "false"}">
              <span class="quote-switcher-ref">${esc(q.reference)}</span>
              <span class="quote-switcher-total">${esc(money(q.totalIncVat))}</span>
              <span class="${STATUS_PILL[q.status]} quote-switcher-pill">${esc(STATUS_LABELS[q.status])}</span>
            </button>`,
        )
        .join("");
      container.appendChild(switcher);
    }
    container.appendChild(detailHost);

    const renderActive = (): void => {
      detailCleanup();
      detailHost.innerHTML = "";
      detailCleanup = mountQuoteDetail(detailHost, quotes[activeIdx]);
      if (switcher) {
        switcher
          .querySelectorAll<HTMLButtonElement>(".quote-switcher-tab")
          .forEach((b) => {
            const on = Number(b.dataset.idx) === activeIdx;
            b.setAttribute("aria-pressed", on ? "true" : "false");
            b.classList.toggle("is-active", on);
          });
      }
    };

    const onSwitcherClick = (e: MouseEvent): void => {
      const tab = (e.target as HTMLElement).closest<HTMLButtonElement>(
        ".quote-switcher-tab",
      );
      if (!tab) return;
      const idx = Number(tab.dataset.idx);
      if (!Number.isNaN(idx) && idx !== activeIdx) {
        activeIdx = idx;
        renderActive();
      }
    };
    if (switcher) switcher.addEventListener("click", onSwitcherClick);

    renderActive();

    return () => {
      detailCleanup();
      if (switcher) {
        switcher.removeEventListener("click", onSwitcherClick);
        if (switcher.parentNode === container) container.removeChild(switcher);
      }
      if (detailHost.parentNode === container) container.removeChild(detailHost);
    };

    // ── Per-quote detail card + approve/sign flow ─────────────────────────────
    // (Hoisted; renders one quote into `host`.)
    function mountQuoteDetail(host: HTMLElement, quote: CustomerQuote): SectionCleanup {
    const ac = new AbortController();

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const card = document.createElement("section");
    card.className = "cust-card quote-card";
    card.setAttribute("aria-labelledby", "quote-title");

    const expiry = expiryInfo(quote);
    const grouped = groupByRoom(quote);
    const projectName = (quote.projectName ?? "").trim();
    const showApproveButton = quote.status === "sent";
    const hasTerms =
      typeof quote.termsConditions === "string" &&
      quote.termsConditions.trim() !== "";

    card.innerHTML = `
      <header class="quote-head">
        <div class="quote-head-text">
          <h2 id="quote-title" class="cust-card-title quote-title">
            Your quote (${esc(quote.reference)})
          </h2>
          ${projectName !== "" ? `<p class="quote-project">${esc(projectName)}</p>` : ""}
        </div>
        <span class="${STATUS_PILL[quote.status]} quote-status-pill">
          ${esc(STATUS_LABELS[quote.status])}
        </span>
      </header>

      ${renderQuoteHeader(quote)}

      ${
        quote.introNote
          ? `<p class="quote-note quote-note--intro">${esc(quote.introNote)}</p>`
          : ""
      }

      <div class="quote-rooms" data-role="rooms">
        ${grouped.map(renderRoomBlock).join("")}
      </div>

      <dl class="quote-totals">
        <div class="quote-totals-row">
          <dt>Subtotal</dt>
          <dd>${esc(money(quote.subtotal))}</dd>
        </div>
        ${
          quote.vatAmount > 0
            ? `<div class="quote-totals-row">
                 <dt>VAT</dt>
                 <dd>${esc(money(quote.vatAmount))}</dd>
               </div>`
            : ""
        }
        ${
          quote.deliveryCharge > 0
            ? `<div class="quote-totals-row">
                 <dt>Delivery</dt>
                 <dd>${esc(money(quote.deliveryCharge))}</dd>
               </div>`
            : ""
        }
        <div class="quote-totals-row quote-totals-grand">
          <dt>${quote.vatAmount > 0 ? "Grand total (inc. VAT)" : "Total"}</dt>
          <dd>${esc(money(quote.totalIncVat))}</dd>
        </div>
      </dl>
      ${
        quote.retailerVatNumber
          ? `<p class="quote-vat-no">VAT No: ${esc(quote.retailerVatNumber)}</p>`
          : ""
      }

      ${
        quote.closingNote
          ? `<p class="quote-note quote-note--closing">${esc(quote.closingNote)}</p>`
          : ""
      }

      ${
        // Bank transfer details. The server sends `bankDetails` ONLY on a quote
        // this customer has accepted, so its presence is the permission — no
        // acceptance check is repeated here (repeating it would risk the two
        // checks disagreeing). Retailer-entered text, escaped; nothing here is
        // charged or reconciled by the platform.
        quote.bankDetails
          ? `<section class="quote-bank" aria-labelledby="quote-bank-h">
               <h3 class="quote-bank-h" id="quote-bank-h">Paying by bank transfer</h3>
               <dl class="quote-bank-rows">
                 <div class="quote-bank-row">
                   <dt>Account name</dt><dd>${esc(quote.bankDetails.accountName)}</dd>
                 </div>
                 ${
                   quote.bankDetails.sortCode
                     ? `<div class="quote-bank-row">
                          <dt>Sort code</dt><dd>${esc(quote.bankDetails.sortCode)}</dd>
                        </div>`
                     : ""
                 }
                 <div class="quote-bank-row">
                   <dt>Account number</dt><dd>${esc(quote.bankDetails.accountNumber)}</dd>
                 </div>
               </dl>
               ${
                 quote.bankDetails.referenceNote
                   ? `<p class="quote-bank-ref">${esc(quote.bankDetails.referenceNote)}</p>`
                   : ""
               }
             </section>`
          : ""
      }

      ${
        expiry !== null
          ? expiry.expired
            ? `<p class="quote-valid quote-valid--expired">This quote expired on <strong>${esc(expiry.label)}</strong>. You can still accept it — or message us for an updated quote.</p>`
            : `<p class="quote-valid">Valid until <strong>${esc(expiry.label)}</strong></p>`
          : ""
      }

      ${
        hasTerms
          ? `<details class="quote-terms" data-role="terms">
              <summary>View terms &amp; conditions</summary>
              <div class="quote-terms-body">${esc(quote.termsConditions ?? "")}</div>
            </details>`
          : ""
      }

      <div class="quote-actions" data-role="actions">
        <button type="button" class="cust-btn cust-btn-secondary quote-btn-pdf" data-role="download-pdf">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="12" y1="18" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <polyline points="9 15 12 18 15 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Download PDF
        </button>
        <button type="button" class="cust-btn cust-btn-secondary quote-btn-discuss" data-role="discuss">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Discuss
        </button>
        ${
          showApproveButton
            ? `<button type="button" class="cust-btn quote-btn-approve" data-role="approve">
                 Approve quote
               </button>
               <button type="button" class="cust-btn cust-btn-secondary quote-btn-decline" data-role="decline">
                 Decline
               </button>`
            : ""
        }
        ${
          quote.status === "approved"
            ? `<span class="cust-pill-status green quote-approved-pill">Approved</span>
               <button type="button" class="cust-btn cust-btn-secondary quote-btn-withdraw" data-role="withdraw">
                 Withdraw approval
               </button>`
            : ""
        }
      </div>
    `;

    host.appendChild(card);

    // ── Confirmation dialog (only built if the approve button exists) ───────

    let dialog: HTMLDialogElement | null = null;
    let confirmCheckbox: HTMLInputElement | null = null;
    let confirmBtn: HTMLButtonElement | null = null;
    let cancelBtn: HTMLButtonElement | null = null;
    let closeBtn: HTMLButtonElement | null = null;
    let approveBtn: HTMLButtonElement | null = null;
    let declineBtn: HTMLButtonElement | null = null;
    let sigCanvas: HTMLCanvasElement | null = null;
    let sigClearBtn: HTMLButtonElement | null = null;
    let sigHint: HTMLElement | null = null;
    let sigHasInk = false;

    function buildDialog(): HTMLDialogElement {
      const fullName = [ctx.project.customer.firstName, ctx.project.customer.lastName]
        .filter((s): s is string => typeof s === "string" && s.trim() !== "")
        .join(" ")
        .trim();

      const d = document.createElement("dialog");
      d.className = "quote-dialog";
      d.innerHTML = `
        <form method="dialog" class="quote-dialog-form">
          <header class="quote-dialog-head">
            <h3>Sign &amp; approve ${esc(quote.reference)}</h3>
            <button type="button" class="quote-dialog-close" data-role="close" aria-label="Close">&times;</button>
          </header>
          <div class="quote-dialog-body">
            <p>
              You're about to approve a quote totalling
              <strong>${esc(money(quote.totalIncVat))}</strong> (inc. VAT).
              Once approved, ${esc(ctx.project.retailer.businessName || "your retailer")} will be
              notified and will send you payment instructions.
            </p>
            <div class="quote-sig" data-role="signature">
              <label class="quote-sig-label" for="quote-sig-pad">
                Sign below to confirm
                ${fullName !== "" ? `(<strong>${esc(fullName)}</strong>)` : ""}
              </label>
              <div class="quote-sig-pad-wrap">
                <canvas
                  id="quote-sig-pad"
                  class="quote-sig-pad"
                  data-role="sig-canvas"
                  aria-label="Signature pad — sign with mouse or finger"
                ></canvas>
                <span class="quote-sig-hint" data-role="sig-hint">Sign here</span>
              </div>
              <div class="quote-sig-controls">
                <button type="button" class="quote-sig-clear" data-role="sig-clear">Clear signature</button>
              </div>
            </div>
            <label class="quote-dialog-check">
              <input type="checkbox" data-role="confirm-check" />
              <span>I have read and accept the terms &amp; conditions.</span>
            </label>
          </div>
          <footer class="quote-dialog-footer">
            <button type="button" class="cust-btn cust-btn-secondary" data-role="cancel">Cancel</button>
            <button type="button" class="cust-btn quote-dialog-confirm" data-role="confirm" disabled>
              Confirm approval
            </button>
          </footer>
        </form>
      `;
      card.appendChild(d);
      return d;
    }

    if (showApproveButton) {
      dialog = buildDialog();
      confirmCheckbox = dialog.querySelector<HTMLInputElement>(
        "[data-role='confirm-check']",
      );
      confirmBtn = dialog.querySelector<HTMLButtonElement>(
        "[data-role='confirm']",
      );
      cancelBtn = dialog.querySelector<HTMLButtonElement>(
        "[data-role='cancel']",
      );
      closeBtn = dialog.querySelector<HTMLButtonElement>(
        "[data-role='close']",
      );
      approveBtn = card.querySelector<HTMLButtonElement>(
        "[data-role='approve']",
      );
      declineBtn = card.querySelector<HTMLButtonElement>(
        "[data-role='decline']",
      );
      sigCanvas = dialog.querySelector<HTMLCanvasElement>(
        "[data-role='sig-canvas']",
      );
      sigClearBtn = dialog.querySelector<HTMLButtonElement>(
        "[data-role='sig-clear']",
      );
      sigHint = dialog.querySelector<HTMLElement>("[data-role='sig-hint']");
    }

    // ── Signature pad ───────────────────────────────────────────────────────

    function setupSignaturePad(canvas: HTMLCanvasElement): () => void {
      const ctx2d = canvas.getContext("2d");
      if (!ctx2d) return () => {};

      let drawing = false;
      let lastX = 0;
      let lastY = 0;

      function resizeCanvas(): void {
        if (!ctx2d) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        // Preserve any existing strokes by snapshotting before resize.
        const snapshot =
          canvas.width > 0 && canvas.height > 0
            ? canvas.toDataURL("image/png")
            : null;
        canvas.width = Math.max(1, Math.round(rect.width * dpr));
        canvas.height = Math.max(1, Math.round(rect.height * dpr));
        ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx2d.lineWidth = 2;
        ctx2d.lineCap = "round";
        ctx2d.lineJoin = "round";
        ctx2d.strokeStyle = "#111827";
        if (snapshot && sigHasInk) {
          const img = new Image();
          img.onload = () => {
            ctx2d.drawImage(img, 0, 0, rect.width, rect.height);
          };
          img.src = snapshot;
        }
      }

      function pointerPos(e: PointerEvent): { x: number; y: number } {
        const rect = canvas.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
      }

      function onPointerDown(e: PointerEvent): void {
        if (!ctx2d) return;
        e.preventDefault();
        canvas.setPointerCapture?.(e.pointerId);
        drawing = true;
        const p = pointerPos(e);
        lastX = p.x;
        lastY = p.y;
        // Draw a dot for taps.
        ctx2d.beginPath();
        ctx2d.arc(p.x, p.y, 1, 0, Math.PI * 2);
        ctx2d.fillStyle = "#111827";
        ctx2d.fill();
        if (!sigHasInk) {
          sigHasInk = true;
          if (sigHint) sigHint.style.display = "none";
          updateConfirmEnabled();
        }
      }

      function onPointerMove(e: PointerEvent): void {
        if (!drawing || !ctx2d) return;
        e.preventDefault();
        const p = pointerPos(e);
        ctx2d.beginPath();
        ctx2d.moveTo(lastX, lastY);
        ctx2d.lineTo(p.x, p.y);
        ctx2d.stroke();
        lastX = p.x;
        lastY = p.y;
      }

      function onPointerUp(e: PointerEvent): void {
        drawing = false;
        try {
          canvas.releasePointerCapture?.(e.pointerId);
        } catch {
          /* ignore */
        }
      }

      canvas.addEventListener("pointerdown", onPointerDown);
      canvas.addEventListener("pointermove", onPointerMove);
      canvas.addEventListener("pointerup", onPointerUp);
      canvas.addEventListener("pointercancel", onPointerUp);
      canvas.addEventListener("pointerleave", onPointerUp);
      window.addEventListener("resize", resizeCanvas);

      // Initial sizing happens once the dialog opens (canvas has 0 width
      // until then). We call resizeCanvas at openDialog() and once here as a
      // safety net.
      resizeCanvas();

      return () => {
        canvas.removeEventListener("pointerdown", onPointerDown);
        canvas.removeEventListener("pointermove", onPointerMove);
        canvas.removeEventListener("pointerup", onPointerUp);
        canvas.removeEventListener("pointercancel", onPointerUp);
        canvas.removeEventListener("pointerleave", onPointerUp);
        window.removeEventListener("resize", resizeCanvas);
      };
    }

    function clearSignature(): void {
      if (!sigCanvas) return;
      const c2d = sigCanvas.getContext("2d");
      if (!c2d) return;
      c2d.clearRect(0, 0, sigCanvas.width, sigCanvas.height);
      sigHasInk = false;
      if (sigHint) sigHint.style.display = "";
      updateConfirmEnabled();
    }

    function updateConfirmEnabled(): void {
      if (!confirmBtn) return;
      const checked = confirmCheckbox?.checked === true;
      confirmBtn.disabled = !(checked && sigHasInk);
    }

    let teardownSig: () => void = () => {};
    if (sigCanvas) teardownSig = setupSignaturePad(sigCanvas);

    // ── Behaviour ───────────────────────────────────────────────────────────

    function openDialog(): void {
      if (!dialog) return;
      if (confirmCheckbox) confirmCheckbox.checked = false;
      clearSignature();
      if (confirmBtn) confirmBtn.disabled = true;
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      // Canvas needs sizing once it's actually visible (dialogs start at 0
      // dimensions before showModal). RAF ensures layout has settled.
      if (sigCanvas) {
        requestAnimationFrame(() => {
          const c = sigCanvas;
          if (!c) return;
          const rect = c.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          c.width = Math.max(1, Math.round(rect.width * dpr));
          c.height = Math.max(1, Math.round(rect.height * dpr));
          const ctx2d = c.getContext("2d");
          if (ctx2d) {
            ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx2d.lineWidth = 2;
            ctx2d.lineCap = "round";
            ctx2d.lineJoin = "round";
            ctx2d.strokeStyle = "#111827";
          }
        });
      }
    }

    function closeDialog(): void {
      if (!dialog) return;
      if (dialog.open) dialog.close();
    }

    async function approveQuote(): Promise<void> {
      if (!confirmBtn) return;
      const originalLabel = confirmBtn.textContent ?? "Confirm approval";
      confirmBtn.disabled = true;
      confirmBtn.textContent = "Approving…";

      try {
        // Endpoint confirmed: POST /api/customer/quotes/:ref/accept
        // (kitchenos-api/modules/customer_portal.js:1210). Token /
        // session is carried by the cookie via api-client's
        // credentials: "include".
        //
        // We attach the signature as a PNG data URL. The legacy endpoint
        // currently ignores extra body fields; the new modular endpoint
        // will persist `signatureDataUrl` and `signedName` against the
        // quote acceptance record (immutable audit trail — defraud).
        const signatureDataUrl = sigCanvas ? sigCanvas.toDataURL("image/png") : null;
        const signedName = [
          ctx.project.customer.firstName,
          ctx.project.customer.lastName,
        ]
          .filter((s): s is string => typeof s === "string" && s.trim() !== "")
          .join(" ")
          .trim() || null;

        await ctx.api<unknown>(
          `/customer/quotes/${encodeURIComponent(quote.reference)}/accept`,
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({
              signatureDataUrl,
              signedName,
              signedAt: new Date().toISOString(),
            }),
          },
        );

        ctx.showToast(
          "Quote approved — you'll receive payment instructions shortly.",
          "success",
        );
        closeDialog();
        // Re-fetch the project so the chrome can re-render this section
        // with the new "approved" status (and remove the approve button).
        await ctx.refresh();
      } catch (err) {
        if (isAbortError(err)) return;
        // If the endpoint is missing in the legacy server we still want
        // to give the customer a graceful message rather than a hard
        // failure. The 2026-05 server has it, but we defend anyway.
        if (err instanceof ApiError && err.status === 404) {
          console.info(
            "[quote] /api/customer/quotes/:ref/accept not available; stubbing optimistic toast.",
          );
          ctx.showToast(
            "Quote approval recorded. Your retailer will follow up shortly.",
            "info",
          );
          closeDialog();
        } else {
          console.error("[quote] approve failed", err);
          ctx.showToast("Could not approve the quote — please try again.", "error");
          confirmBtn.disabled = false;
          confirmBtn.textContent = originalLabel;
        }
      }
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    // ── Withdraw approval (before payment) ──────────────────────────────────
    const withdrawBtn = card.querySelector<HTMLButtonElement>("[data-role='withdraw']");
    function confirmWithdraw(): Promise<boolean> {
      return new Promise((resolve) => {
        const d = document.createElement("dialog");
        d.className = "quote-dialog";
        d.innerHTML = `
          <form method="dialog" class="quote-dialog-form">
            <header class="quote-dialog-head"><h3>Withdraw approval</h3></header>
            <div class="quote-dialog-body">
              <p>Withdraw your approval of quote ${esc(quote.reference)}? It returns to your review and your retailer is notified. You can approve it again any time before a payment is made.</p>
            </div>
            <footer class="quote-dialog-footer">
              <button type="button" class="cust-btn cust-btn-secondary" data-role="w-cancel">Cancel</button>
              <button type="button" class="cust-btn quote-dialog-confirm" data-role="w-confirm">Withdraw approval</button>
            </footer>
          </form>`;
        card.appendChild(d);
        const done = (v: boolean): void => { try { d.close(); } catch { /* noop */ } d.remove(); resolve(v); };
        d.querySelector<HTMLButtonElement>("[data-role='w-cancel']")?.addEventListener("click", () => done(false));
        d.querySelector<HTMLButtonElement>("[data-role='w-confirm']")?.addEventListener("click", () => done(true));
        d.addEventListener("cancel", () => done(false));
        if (typeof d.showModal === "function") d.showModal(); else d.setAttribute("open", "");
      });
    }
    async function onWithdraw(): Promise<void> {
      if (!(await confirmWithdraw())) return;
      if (withdrawBtn) { withdrawBtn.disabled = true; withdrawBtn.textContent = "Withdrawing…"; }
      try {
        await ctx.api<unknown>(
          `/customer/quotes/${encodeURIComponent(quote.reference)}/withdraw`,
          { method: "POST", signal: ac.signal },
        );
        ctx.showToast("Approval withdrawn — the quote is open again.", "success");
        await ctx.refresh();
      } catch (err) {
        if (isAbortError(err)) return;
        const msg = err instanceof ApiError && err.status === 409
          ? "This quote can no longer be withdrawn — a payment may already have been made. Please contact your retailer."
          : "Could not withdraw approval — please try again.";
        ctx.showToast(msg, "error");
        if (withdrawBtn) { withdrawBtn.disabled = false; withdrawBtn.textContent = "Withdraw approval"; }
      }
    }
    withdrawBtn?.addEventListener("click", () => { void onWithdraw(); });

    const onApproveClick = (): void => openDialog();
    const onDeclineClick = (): void => {
      // V1: stub — full reasons-flow is out of scope.
      ctx.showToast(
        "To decline this quote, please reply to your retailer with your reasons.",
        "info",
      );
    };

    // Download PDF — render a printable HTML representation of the quote
    // and open the browser's print dialog. Mirrors customer-live.js:510-609.
    // No external libs (no jsPDF) — the browser's "Save as PDF" produces a
    // dependable, properly-styled PDF and avoids shipping ~1MB of extra JS.
    const onDownloadPdf = (): void => {
      const html = buildPrintableQuoteHtml(
        quote,
        ctx.project.retailer.businessName || "Your retailer",
        ctx.project.customer.firstName || "",
      );
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        // Pop-up blocked — fall back to an anchor click.
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          try { document.body.removeChild(a); } catch { /* ignore */ }
        }, 100);
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    };

    // Discuss — jump straight to Messages so the customer can reply with
    // their questions about the quote. Mirrors source customer-live.js:484.
    const onDiscussClick = (): void => {
      window.dispatchEvent(
        new CustomEvent("cust-nav", { detail: { page: "messages" } }),
      );
    };
    const onCheckChange = (): void => {
      updateConfirmEnabled();
    };
    const onSigClear = (): void => clearSignature();
    const onConfirmClick = (): void => {
      if (confirmCheckbox && !confirmCheckbox.checked) return;
      void approveQuote();
    };
    const onCancelClick = (): void => closeDialog();
    const onCloseClick = (): void => closeDialog();
    const onDialogClick = (e: MouseEvent): void => {
      // Click on backdrop closes the dialog.
      if (e.target === dialog) closeDialog();
    };

    const pdfBtn = card.querySelector<HTMLButtonElement>(
      "[data-role='download-pdf']",
    );
    const discussBtn = card.querySelector<HTMLButtonElement>(
      "[data-role='discuss']",
    );

    if (pdfBtn) pdfBtn.addEventListener("click", onDownloadPdf);
    if (discussBtn) discussBtn.addEventListener("click", onDiscussClick);
    if (approveBtn) approveBtn.addEventListener("click", onApproveClick);
    if (declineBtn) declineBtn.addEventListener("click", onDeclineClick);
    if (confirmCheckbox)
      confirmCheckbox.addEventListener("change", onCheckChange);
    if (confirmBtn) confirmBtn.addEventListener("click", onConfirmClick);
    if (cancelBtn) cancelBtn.addEventListener("click", onCancelClick);
    if (closeBtn) closeBtn.addEventListener("click", onCloseClick);
    if (sigClearBtn) sigClearBtn.addEventListener("click", onSigClear);
    if (dialog) dialog.addEventListener("click", onDialogClick);

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      ac.abort();
      teardownSig();
      if (pdfBtn) pdfBtn.removeEventListener("click", onDownloadPdf);
      if (discussBtn) discussBtn.removeEventListener("click", onDiscussClick);
      if (approveBtn) approveBtn.removeEventListener("click", onApproveClick);
      if (declineBtn) declineBtn.removeEventListener("click", onDeclineClick);
      if (confirmCheckbox)
        confirmCheckbox.removeEventListener("change", onCheckChange);
      if (confirmBtn) confirmBtn.removeEventListener("click", onConfirmClick);
      if (cancelBtn) cancelBtn.removeEventListener("click", onCancelClick);
      if (closeBtn) closeBtn.removeEventListener("click", onCloseClick);
      if (sigClearBtn) sigClearBtn.removeEventListener("click", onSigClear);
      if (dialog) {
        dialog.removeEventListener("click", onDialogClick);
        if (dialog.open) dialog.close();
      }
      if (card.parentNode === host) {
        host.removeChild(card);
      }
    };
    } // end mountQuoteDetail
  },
};

export default quoteSection;
